import {LitElement, unsafeCSS} from "lit";
import {customElement, property, query} from "lit/decorators.js";
import {html, unsafeStatic} from "lit/static-html.js";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from "@floating-ui/dom";
import styles from "./IdsTooltipElement.css?inline";
import {ROOT_TAG} from "../../globals";

const tagName = `${ROOT_TAG}-interstate-tooltip`;
export const InterstateTooltip = unsafeStatic(tagName);

/**
 * A simple tooltip component that displays content on hover.
 * This is a DIY implementation to avoid dependencies on external Interstate versions.
 */
@customElement(tagName)
export class IdsTooltipElement extends LitElement {
  static styles = unsafeCSS(styles);

  @query(".ids-tooltip")
  private tooltipElement?: HTMLDivElement;

  private cleanupAutoUpdate: (() => void) | undefined;

  @property({type: String})
  text: string = "";

  @property({type: String})
  position: "top" | "bottom" | "left" | "right" | "auto" = "top";

  @property({type: String})
  size: "sm" | "md" | "lg" = "sm";

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("mouseenter", this.handleShow);
    this.addEventListener("mouseleave", this.handleHide);
    this.addEventListener("focusin", this.handleShow);
    this.addEventListener("focusout", this.handleHide);
  }

  disconnectedCallback() {
    this.removeEventListener("mouseenter", this.handleShow);
    this.removeEventListener("mouseleave", this.handleHide);
    this.removeEventListener("focusin", this.handleShow);
    this.removeEventListener("focusout", this.handleHide);
    this.stopAutoUpdate();
    super.disconnectedCallback();
  }

  private readonly handleShow = async () => {
    await this.updateTooltipPosition();

    const tooltip = this.tooltipElement;
    if (!tooltip) return;

    this.stopAutoUpdate();
    this.cleanupAutoUpdate = autoUpdate(this, tooltip, () => {
      void this.updateTooltipPosition();
    });
  };

  private readonly handleHide = () => {
    this.stopAutoUpdate();
  };

  private stopAutoUpdate() {
    this.cleanupAutoUpdate?.();
    this.cleanupAutoUpdate = undefined;
  }
  /**
   * Keep the tooltip fully visible within the viewport.
   *
   * Without collision handling, the tooltip can be clipped when the trigger is
   * near the left/right edge of the screen or when the tooltip content is wide.
   */
  private async updateTooltipPosition() {
    await this.updateComplete;
    const tooltip = this.tooltipElement;
    if (!tooltip) return;

    const {
      x,
      y,
      placement: computedPlacement
    } = this.position === "auto"
      ? await computePosition(this, tooltip, {
          placement: "top",
          strategy: "fixed",
          middleware: [
            offset(8),
            flip({fallbackPlacements: ["bottom", "right", "left"]}),
            shift({padding: 8})
          ]
        })
      : await computePosition(this, tooltip, {
          placement: this.position,
          strategy: "fixed",
          middleware: [offset(8), shift({padding: 8})]
        });

    tooltip.style.position = "fixed";
    tooltip.style.left = `${x}px`;
    tooltip.style.top = `${y}px`;
    tooltip.dataset.idsPos = computedPlacement;
  }

  render() {
    return html`
      <slot class="inline-flex"></slot>
      <div
        class="ids-tooltip bg-gray-800 text-white rounded px-3 py-2 text-sm whitespace-nowrap invisible opacity-0 z-[1000] transition-opacity duration-200 pointer-events-none"
        role="tooltip"
        data-ids-size=${this.size}
        data-ids-pos=${this.position}>
        ${this.text}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: IdsTooltipElement;
  }
}
